import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import { Typography } from "@mui/material";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useRevealWage } from "../../hooks/useRevealWage";
import { FONT_SIZE, INK } from "../../theme/tokens";
import type { AnniePickupRow } from "../../utils/annieStream";
import { formatPredictedCost } from "../../utils/costPrediction";
import { formatPtoTimeRange } from "../../utils/dateUtils";
import type { EmploymentType } from "../../utils/models";
import { getEmploymentTypeDisplayName } from "../../utils/utilFunctions";
import { MetaChip } from "../UIToolkit/Badges/MetaChip";
import { OvertimeCell } from "../UIToolkit/Cells/OvertimeCell/OvertimeCell";
import { shouldShowOtColumn } from "../UIToolkit/Cells/OvertimeCell/otVisibility";
import { RequestedAtCell } from "../UIToolkit/Cells/RequestedAtCell/RequestedAtCell";
import { SeniorityCell } from "../UIToolkit/Cells/SeniorityCell/SeniorityCell";
import { type DecisionRowItem, DecisionRowList, type DecisionRowMetric } from "../UIToolkit/Tables/DecisionRowList/DecisionRowList";
import { toAnniePickupDepartmentTime } from "./anniePickupTableTime";

type Props = {
	rows: AnniePickupRow[];
	/** Department timezone — accepted (partial) windows and applied-at render in the dept-local clock, like the primary modal. */
	timezone: string;
	/**
	 * When provided, each row becomes a selectable button (keyboard + hover + focus) that calls back with its
	 * `nurse_id` — this turns the same rows into the approve nurse-picker (clicking a row round-trips via
	 * /annie/replan). Omitted → the read-only proposal rows.
	 */
	onSelect?: (nurseId: number) => void;
	/** Disables selection (e.g. while a pick is in flight / the picker expired) without changing the layout. */
	selectionDisabled?: boolean;
	dataTestId?: string;
};

const EMPTY_VALUE = "—";

/** A whole hour reads as "36", a part-hour as "36.5" — never "36.0". */
const formatHours = (value: number): string => (Number.isInteger(value) ? value.toString() : value.toFixed(1));

/**
 * Annie's pending open-shift pickups, one decision row per applicant — the chat-panel counterpart of the
 * app's primary approval picker (`MinimizedNurseDataTable`), carrying the same fields it does: cost,
 * overtime, weekly hours, seniority, contract type and when they applied. Every figure goes through the
 * SAME shared renderer as that grid (`OvertimeCell`, `SeniorityCell`, `RequestedAtCell`,
 * `formatPredictedCost`), so a number reads identically in both places.
 *
 * Suppression mirrors the primary approve modal exactly, and only money is ever withheld: the Cost
 * figure drops entirely for hide-wage head nurses (`cost_available=false`) while every other field
 * stays, and the Overtime metric drops for medical/synergy departments (`ot_suppressed`). Both flags are
 * uniform per user / per department, so the metric SET is decided once for the whole list — that is what
 * keeps figures aligned down the rows.
 *
 * Pass `onSelect` to reuse the exact same rows AS the approve nurse-picker; nothing else forks.
 */
export const AnniePickupTable: FC<Props> = ({ rows, timezone, onSelect, selectionDisabled = false, dataTestId = "annie-pickup-table" }) => {
	const { t } = useTranslation();
	// Wages are gated by TWO independent things, and the backend only knows one of them: `cost_available`
	// carries the server-side permission, while `useRevealWage()` also folds in the header's per-user
	// show/hide-wages toggle, which lives in this browser's localStorage and never reaches the backend.
	// Checking only the server flag is how this table kept printing costs on a screen the user had just
	// switched to hidden. Same pairing the primary approval table uses (`hideWage = !useRevealWage()`).
	const canViewWage = useRevealWage();

	if (rows.length === 0) return null;

	// Decided once across all rows (both flags are uniform per user / per department, so any-row checks are
	// equivalent to every-row): no Cost for hide-wage users, no Overtime for medical/synergy departments.
	const showCost = canViewWage && rows.some((row) => row.cost_available);
	const showOt = shouldShowOtColumn({ hasOvertimeData: true, isMedicalDepartment: rows.some((row) => row.ot_suppressed) });

	// The backend orders the picker no-overtime-first (then cheapest, then earliest applied), so row 1 IS
	// the recommendation — the rail says so, and the note says why. Only claimed when it is actually true
	// of the data AND there is something to prefer it over.
	const lead = rows[0];
	const leadIsOnlyNoOt = showOt && lead.overtime_delta === 0 && rows.slice(1).some((row) => row.overtime_delta > 0);
	const leadIsCheapest = showCost && lead.cost !== null && rows.slice(1).every((row) => row.cost === null || row.cost >= (lead.cost ?? 0));
	const leadNote = leadIsOnlyNoOt ? (leadIsCheapest ? t("annie.pickup.lead.noOvertimeLowestCost") : t("annie.pickup.lead.noOvertime")) : undefined;

	const items: DecisionRowItem[] = rows.map((row) => {
		const acceptedStart = toAnniePickupDepartmentTime(row.accepted_start, timezone);
		const acceptedEnd = toAnniePickupDepartmentTime(row.accepted_end, timezone);
		const partialWindow = row.is_partial && acceptedStart && acceptedEnd ? formatPtoTimeRange(acceptedStart, acceptedEnd) : null;

		const metrics: DecisionRowMetric[] = [
			{
				key: "hours",
				label: t("annie.pickup.metric.hours"),
				testId: `annie-pickup-hours-${row.nurse_id}`,
				value:
					row.weekly_hours_after > 0
						? t("annie.pickup.metric.hours.value", { before: formatHours(row.weekly_hours_before), after: formatHours(row.weekly_hours_after) })
						: EMPTY_VALUE,
			},
		];
		if (showOt) {
			metrics.push({
				key: "ot",
				label: t("nurses.matrix.ot"),
				testId: `annie-pickup-ot-${row.nurse_id}`,
				value: (
					<OvertimeCell
						overtimeHours={row.overtime_delta}
						weeklyOvertimeHoursBeforeChange={row.overtime_weekly_after - row.overtime_delta}
						showWeeklyOt
						isAdd
						t={t}
					/>
				),
			});
		}
		metrics.push({
			key: "seniority",
			label: t("nurses.matrix.seniority"),
			testId: `annie-pickup-seniority-${row.nurse_id}`,
			value: <SeniorityCell nurseStartDate={toSeniorityDate(row.seniority_start_date)} t={t} />,
		});
		metrics.push({
			key: "applied",
			label: t("nurses.matrix.requested.at"),
			testId: `annie-pickup-applied-${row.nurse_id}`,
			value: <RequestedAtCell requestedAt={toAnniePickupDepartmentTime(row.applied_at, timezone)} compact />,
		});

		return {
			id: row.nurse_id,
			// `nurse_name` is the bare name; `nurse_label` (which appends ", RN") is the pre-change fallback
			// for a resumed proposal card that predates the split.
			title: row.nurse_name || row.nurse_label,
			rowTestId: `annie-pickup-row-${row.nurse_id}`,
			selectTestId: `annie-pickup-select-${row.nurse_id}`,
			chips: (
				<>
					{row.role && <MetaChip label={row.role} tone="primary" dataTestId={`annie-pickup-role-${row.nurse_id}`} />}
					{row.employment_type && (
						// The wire carries the raw enum code ("fullTime"); the app's own formatter owns the label,
						// so a contract type reads the same here as in the nurse grid and the approval dialog.
						<MetaChip label={getEmploymentTypeDisplayName(row.employment_type as EmploymentType)} dataTestId={`annie-pickup-contract-${row.nurse_id}`} />
					)}
					{row.is_partial && (
						<MetaChip
							label={partialWindow ? t("annie.pickup.partial.window", { window: partialWindow }) : t("partial")}
							tone="warning"
							icon={<ScheduleIcon sx={{ fontSize: "inherit" }} />}
							dataTestId={`annie-pickup-partial-chip-${row.nurse_id}`}
						/>
					)}
				</>
			),
			trailing: showCost ? (
				<Typography
					data-testid={`annie-pickup-cost-${row.nurse_id}`}
					sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
				>
					{canViewWage && row.cost_available && row.cost !== null ? formatPredictedCost(row.cost, { isEstimated: row.cost_is_estimated }) : EMPTY_VALUE}
				</Typography>
			) : undefined,
			// Only the lead row carries it — the list renders a note for the lead only, and handing the same
			// sentence to every row would make it look row-specific to the next reader.
			note: row.nurse_id === lead.nurse_id ? leadNote : undefined,
			metrics,
		};
	});

	return (
		<DecisionRowList
			rows={items}
			onSelect={onSelect ? (id) => onSelect(Number(id)) : undefined}
			selectionDisabled={selectionDisabled}
			leadRowId={leadNote ? lead.nurse_id : undefined}
			// 4 metrics when overtime shows, 3 when the department suppresses it — one grid shape for every row.
			metricColumns={showOt ? 4 : 3}
			ariaLabel={t("annie.pickup.list.label")}
			dataTestId={dataTestId}
		/>
	);
};

/**
 * A `date` on the wire (seniority has no clock), so it is read as a plain calendar date, not zoned.
 *
 * A future start date is dropped rather than passed on: `SeniorityCell` diffs against now and would
 * render a negative tenure, where the backend's `seniority_label` reports nothing for the same nurse.
 * Both surfaces show tenure, so they must agree on the one case the data can be wrong in.
 */
const toSeniorityDate = (value: string | DateTime | null): DateTime | undefined => {
	if (value === null) return undefined;
	const dateTime = typeof value === "string" ? DateTime.fromISO(value) : value;
	if (!dateTime.isValid || dateTime > DateTime.now()) return undefined;
	return dateTime;
};
